import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  AI_AVATARS__BASE_PATH,
  TESTTASK_ID,
  TEST_IMAGE_ID,
  LARGE_IMAGE_PATH,
  SMALL_IMAGE_PATH,
} from 'utils/configs';

import createTaskFromGallery from 'services/createTaskFromGallery';
import createTaskFromUploaded from 'services/createTaskFromUploaded';
import getTaskList from 'services/getTaskList';
import goToTheTask from 'services/goToTheTask';
import deleteTask from 'services/deleteTask';
import sendFeedback from 'services/sendFeedback';
import getCachedImageBlobUrl from 'services/getCachedImageBlobUrl';

import defaultInputImage from 'media/images/test_ai_model_input.jpg';
import defaultResultImage from 'media/images/test_ai_model_result.jpg';

const defaultTask = {
  id: TESTTASK_ID,
  images: [
    {
      id: TEST_IMAGE_ID,
      input_image: defaultInputImage,
      input_image_small: defaultInputImage,
      input_image_large: defaultInputImage,
      result_image: defaultResultImage,
      result_image_basic: defaultResultImage,
      result_image_large: defaultResultImage,
    },
  ],
  status: 'test',
};

function getBaseUrl(id) {
  return `${AI_AVATARS__BASE_PATH}/task/${id}/image/`;
}

const processImages = async dataArray => {
  return await Promise.all(
    dataArray.map(async item => {
      const imagesWithBlobs = await Promise.all(
        item.images.map(async img => ({
          ...img,
          input_image: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.input_image,
            ''
          ),
          input_image_large: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.input_image,
            LARGE_IMAGE_PATH
          ),
          input_image_small: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.input_image,
            SMALL_IMAGE_PATH
          ),
          result_image_basic: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.result_image,
            ''
          ),
          result_image_large: await getCachedImageBlobUrl(
            getBaseUrl,
            item.id,
            img.result_image,
            LARGE_IMAGE_PATH
          ),
        }))
      );

      return {
        ...item,
        images: imagesWithBlobs,
      };
    })
  );
};

export const createAiAvatarsTaskFromUploaded = createAsyncThunk(
  'aigenerateavatars/createFromUploaded',

  async (taskData, thunkAPI) => {
    try {
      const formData = new FormData();

      Object.entries(taskData).forEach(([key, value]) => {
        if (key !== 'input_files') {
          formData.append(key, value);
        } else {
          value.forEach((item, index) => {
            formData.append('input_files', value[index]);
          });
        }
      });

      const data = await createTaskFromUploaded(
        AI_AVATARS__BASE_PATH,
        formData,
        thunkAPI
      );

      return data;
    } catch (error) {
      console.error('❌ Error of creating:', error);
    }
  }
);

export const createAiAvatarsTaskFromGallery = createAsyncThunk(
  'aigenerateavatars/createFromGallery',

  async (taskData, thunkAPI) => {
    try {
      const data = await createTaskFromGallery(
        AI_AVATARS__BASE_PATH,
        taskData,
        thunkAPI
      );

      return data;
    } catch (error) {
      console.error('❌ Error of creating:', error);
    }
  }
);

export const getAiAvatarsTaskList = createAsyncThunk(
  'aigenerateavatars/gallery',

  async (_, thunkAPI) => {
    try {
      let processedData = [];
      const data = await getTaskList(AI_AVATARS__BASE_PATH, thunkAPI);

      if (data.length > 0) {
        processedData = await processImages(data);
      } else {
        processedData = [defaultTask];
      }

      return processedData;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const goToTheAiAvatarsTask = createAsyncThunk(
  'aigenerateavatars/goToTheTask',

  async (taskId, thunkAPI) => {
    let processedData = [];
    try {
      if (taskId === TESTTASK_ID) {
        processedData = [defaultTask];
      } else {
        const data = await goToTheTask(AI_AVATARS__BASE_PATH, taskId, thunkAPI);

        processedData = await processImages([data]);
      }

      return processedData[0];
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const removeAiAvatarsTask = createAsyncThunk(
  'aigenerateavatars/removeTask',

  async (taskId, thunkAPI) => {
    try {
      const data = deleteTask(AI_AVATARS__BASE_PATH, taskId, thunkAPI);

      return data;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);

export const sendAiAvatarsImageFeedback = createAsyncThunk(
  'aigenerateavatars/imageFeedback',

  async (feedbackBody, thunkAPI) => {
    try {
      const { value, taskId, mediaId } = feedbackBody;
      const toolPath = `${AI_AVATARS__BASE_PATH}/task`;

      const responce = await sendFeedback(
        value,
        toolPath,
        taskId,
        mediaId,
        thunkAPI
      );

      return responce;
    } catch (error) {
      console.error('❌ Error:', error);
    }
  }
);
